import React from 'react'
import { Baby, BookOpen, GraduationCap, Users, Clock, Star, CheckCircle, Calendar } from 'lucide-react'
import Card from '../components/ui/Card'
import Button from '../components/ui/Button'

const Programs = () => {
 const programs = [
  {
    icon: Baby,
    title: "Transición",
    subtitle: "Preescolar (5 años)",
    description: "Preparamos a los más pequeños para su primera experiencia educativa formal con metodologías lúdicas y pedagógicas especializadas.",
    features: [
      "Desarrollo psicomotor",
      "Iniciación a la lectoescritura",
      "Socialización y valores",
      "Actividades lúdicas"
    ],
    ages: "5 años",
    schedule: "Jornada Matinal y Vespertina",
    color: "from-pink-500 to-purple-500",
    bgColor: "bg-pink-50"
  },
  {
    icon: BookOpen,
    title: "Básica Primaria",
    subtitle: "Grados 1° a 5°",
    description: "Fortalecemos las competencias básicas en matemáticas, lenguaje, ciencias y sociales, desarrollando el pensamiento crítico y creativo.",
    features: [
      "Matemáticas y lenguaje",
      "Ciencias naturales",
      "Ciencias sociales",
      "Educación artística"
    ],
    ages: "6 a 10 años",
    schedule: "Vespertina (1° a 4°) - Matinal (5°)",
    color: "from-gardenia-green to-green-600",
    bgColor: "bg-green-50"
  },
  {
    icon: GraduationCap,
    title: "Básica Secundaria",
    subtitle: "Grados 6° a 9°",
    description: "Profundizamos en áreas especializadas preparando a nuestros estudiantes para la educación media y su proyecto de vida.",
    features: [
      "Materias especializadas",
      "Orientación vocacional",
      "Proyectos de investigación",
      "Formación en valores"
    ],
    ages: "11 a 15 años",
    schedule: "Jornada Matinal",
    color: "from-gardenia-red to-red-600",
    bgColor: "bg-red-50"
  },
  {
    icon: Award,
    title: "Media Académica",
    subtitle: "Grados 10° y 11°",
    description: "Preparamos a nuestros estudiantes para la educación superior y el mundo laboral con énfasis en competencias académicas avanzadas.",
    features: [
      "Preparación pruebas Saber",
      "Orientación profesional",
      "Investigación avanzada",
      "Liderazgo y emprendimiento"
    ],
    ages: "16 a 17 años",
    schedule: "Jornada Única",
    color: "from-purple-500 to-purple-600",
    bgColor: "bg-purple-50"
  }
]
  const features = [
    {
      icon: Users,
      title: "Grupos Reducidos",
      description: "Máximo 30 estudiantes por aula para atención personalizada"
    },
    {
      icon: Star,
      title: "Metodología Innovadora",
      description: "Estrategias pedagógicas modernas y efectivas"
    },
    {
      icon: Clock,
      title: "Horarios Flexibles",
      description: "Adaptados a las necesidades de las familias"
    },
    {
      icon: CheckCircle,
      title: "Evaluación Integral",
      description: "Seguimiento continuo del progreso estudiantil"
    }
  ]

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-gardenia-red/10 to-gardenia-green/10">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <div className="mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r from-gardenia-red to-gardenia-green shadow-2xl mb-6">
                <BookOpen className="h-10 w-10 text-white" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gardenia-dark mb-6">
              Nuestros 
              <span className="text-gradient block mt-2">Programas Educativos</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-8">
              Ofrecemos una educación integral desde transición hasta noveno grado, 
              con programas diseñados para cada etapa del desarrollo de nuestros estudiantes, 
              promoviendo la excelencia académica y la formación en valores.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button to="/admisiones" size="lg">
                Proceso de Admisión
              </Button>
              <Button to="/contacto" variant="outline" size="lg">
                Solicitar Información
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gardenia-dark mb-6">
              Características de Nuestros Programas
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Cada programa está diseñado con estándares de calidad que garantizan 
              una educación integral y personalizada.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {features.map((feature, index) => {
              const IconComponent = feature.icon
              return (
                <Card key={index} className="text-center hover-lift" variant="bordered">
                  <div className="mb-4">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-gardenia-red/10 to-gardenia-green/10 mb-4">
                      <IconComponent className="h-8 w-8 text-gardenia-red" />
                    </div>
                    <Card.Title className="text-lg">{feature.title}</Card.Title>
                  </div>
                  <Card.Content className="text-sm">
                    {feature.description}
                  </Card.Content>
                </Card>
              )
            })}
          </div>
        </div>
      </section>

      {/* Programs Detail */}
      <section className="section-padding bg-gradient-to-b from-gray-50 to-white">
        <div className="container-custom">
          <div className="space-y-16">
            {programs.map((program, index) => {
              const IconComponent = program.icon
              const isEven = index % 2 === 0
              
              return (
                <div key={index} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  isEven ? '' : 'lg:grid-flow-col-dense'
                }`}>
                  {/* Content */}
                  <div className={isEven ? '' : 'lg:col-start-2'}>
                    <div className="mb-6">
                      <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r ${program.color} shadow-xl mb-4`}>
                        <IconComponent className="h-8 w-8 text-white" />
                      </div>
                      <h3 className="text-3xl md:text-4xl font-bold text-gardenia-dark mb-2">
                        {program.title}
                      </h3>
                      <p className="text-xl text-gardenia-red font-semibold mb-4">
                        {program.subtitle}
                      </p>
                    </div>

                    <p className="text-gray-600 leading-relaxed mb-6">
                      {program.description}
                    </p>

                    {/* Info Grid */}
                    <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-white rounded-lg shadow-sm">
                      <div className="text-center">
                        <Users className="h-5 w-5 text-gardenia-red mx-auto mb-2" />
                        <div className="text-sm font-semibold text-gardenia-dark">Edades</div>
                        <div className="text-xs text-gray-600">{program.ages}</div>
                      </div>
                      <div className="text-center">
                        <Clock className="h-5 w-5 text-gardenia-green mx-auto mb-2" />
                        <div className="text-sm font-semibold text-gardenia-dark">Horario</div>
                        <div className="text-xs text-gray-600">{program.schedule}</div>
                      </div>
                    </div>

                    <Button to="/admisiones" variant="outline">
                      Información de Admisión
                    </Button>
                  </div>

                  {/* Curriculum Card */}
                  <div className={isEven ? '' : 'lg:col-start-1 lg:row-start-1'}>
                    <Card className={`${program.bgColor}`} padding="lg">
                      <div className="mb-6">
                        <Card.Title className="text-2xl text-gardenia-dark">
                          Plan de Estudios
                        </Card.Title>
                        <Card.Subtitle className="text-gardenia-red">
                          Áreas de Formación
                        </Card.Subtitle>
                      </div>

                      <div className="space-y-3 mb-6">
                        {program.curriculum.map((subject, idx) => (
                          <div key={idx} className="flex items-start space-x-3">
                            <CheckCircle className="h-4 w-4 text-gardenia-green mt-0.5 flex-shrink-0" />
                            <span className="text-sm text-gray-700">{subject}</span>
                          </div>
                        ))}
                      </div>

                      <div className="border-t border-gray-200 pt-6">
                        <h4 className="font-semibold text-gardenia-dark mb-3">Objetivos:</h4>
                        <ul className="space-y-2">
                          {program.objectives.map((objective, idx) => (
                            <li key={idx} className="flex items-start space-x-2 text-sm text-gray-600">
                              <div className="w-1.5 h-1.5 bg-gardenia-red rounded-full mt-2 flex-shrink-0"></div>
                              <span>{objective}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </Card>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Admission CTA */}
      <section className="section-padding bg-gradient-to-r from-gardenia-red to-gardenia-green text-white">
        <div className="container-custom text-center">
          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <Calendar className="h-16 w-16 mx-auto mb-6 opacity-90" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              ¡Admisiones Abiertas 2026!
            </h2>
            <p className="text-lg md:text-xl mb-8 opacity-90 max-w-2xl mx-auto">
              El proceso de admisión para el año escolar 2026 ya está abierto. 
              Inscribe a tu hijo en una de las mejores instituciones educativas de Barranquilla.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto mb-8">
              <div className="bg-white/10 rounded-lg p-4">
                <div className="text-2xl font-bold mb-2">Transición 2026</div>
                <div className="text-sm opacity-90">Para niños de 5 años</div>
              </div>
              <div className="bg-white/10 rounded-lg p-4">
                <div className="text-2xl font-bold mb-2">1° a 9° Grado</div>
                <div className="text-sm opacity-90">Básica Primaria y Bachillerato</div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button to="/admisiones" variant="gold" size="lg">
                Ver Proceso de Admisión
              </Button>
              <Button to="/contacto" variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-gardenia-red">
                Solicitar Información
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Programs